import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import React, { useState, useEffect } from "react";
import Select from "react-select";
// import TextEditor from '../../../feature-module/inventory/texteditor';
import { useToast } from "../../../Toaster/ToastContext";
import PropTypes from "prop-types";
import { apiClient } from "../../../services/apiClient";

const AddPurchases = ({ currentPurchase, onClose, onUpdate, canUpdate = true, canCreate = true }) => {

  const status = [
    { value: "choose", label: "Choose" },
    { value: "received", label: "Received" },
    { value: "pending", label: "Pending" },
  ];

  const [selectedDateModal, setSelectedDateModal] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [referenceNo, setReferenceNo] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [notes, setNotes] = useState("");
  const { showToast } = useToast();
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [suppliersData, branchesData, productsData] = await Promise.all([
          apiClient.get("/get-all-supplier"),
          apiClient.get("/get-all-branch"),
          apiClient.post("/get-all-products"),
        ]);

        const supplierOptions = [
          { value: "choose", label: "Choose Supplier" },
          ...suppliersData.data.map((supplier) => ({
            value: supplier.id,
            label: supplier.supplier_name,
          })),
        ];
        setSuppliers(supplierOptions);

        const branchOptions = [
          { value: "choose", label: "Choose Branch" },
          ...branchesData.data.map((branch) => ({
            value: branch.id,
            label: branch.name,
          })),
        ];
        setBranches(branchOptions);

        const productOptions = productsData.data || [];
        setAllProducts(productOptions);

        if (currentPurchase) {
          setSelectedSupplier(
            supplierOptions.find((s) => s.value === currentPurchase.supplier_id) || null
          );
          setSelectedBranch(
            branchOptions.find((b) => b.value === currentPurchase.branch_id) || null
          );
          setSelectedDateModal(
            currentPurchase.date ? new Date(currentPurchase.date) : null
          );
          setReferenceNo(currentPurchase.reference || "");
          setSelectedStatus(
            status.find((s) => s.value === currentPurchase.status) || null
          );
          setNotes(currentPurchase.note || "");

          const mappedItems = [];
          const productItems = {};

          currentPurchase.items.forEach((item) => {
            const product = productOptions.find((p) => p.id === item.product_id);
            const productId = item.product_id;
            const variant = item.variant_id
              ? {
                  id: item.variant_id,
                  name: product?.variants?.find((v) => v.id === item.variant_id)?.name || "N/A",
                  variantValue:
                    product?.variants?.find((v) => v.id === item.variant_id)?.variant_value || "",
                  qty: parseInt(item.quantity) || 1,
                  purchasePrice: parseFloat(item.price) || 0,
                  unitCost: parseFloat(item.price) || 0,
                  totalCost: parseFloat(item.subtotal) || 0,
                }
              : null;

            if (!productItems[productId]) {
              productItems[productId] = {
                productId,
                product: product?.name || "Unknown Product",
                qty: variant ? 0 : parseInt(item.quantity) || 1,
                purchasePrice: variant ? 0 : parseFloat(item.price) || 0,
                unitCost: variant ? 0 : parseFloat(item.price) || 0,
                totalCost: variant ? 0 : parseFloat(item.subtotal) || 0,
                variants: variant ? [variant] : [],
              };
            } else if (variant) {
              productItems[productId].variants.push(variant);
            }
          });

          Object.values(productItems).forEach((item) => {
            if (item.variants.length > 0) {
              item.totalCost = item.variants.reduce((sum, v) => sum + (v.totalCost || 0), 0);
            }
            mappedItems.push(item);
          });

          setTableData(mappedItems);
        } else {
          setSelectedSupplier(null);
          setSelectedBranch(null);
          setSelectedDateModal(null);
          setReferenceNo("");
          setSelectedStatus(null);
          setNotes("");
          setTableData([]);
          setErrors({});
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        showToast("Failed to fetch data", "error", "Data Fetch");
      }
    };

    fetchData();
  }, [currentPurchase]);

  const validateForm = () => {
    const newErrors = {};
    if (!selectedSupplier || selectedSupplier.value === "choose") {
      newErrors.supplier_id = "Supplier is required";
    }
    if (!selectedBranch || selectedBranch.value === "choose") {
      newErrors.branch_id = "Branch is required";
    }
    if (!selectedDateModal) {
      newErrors.purchase_date = "Purchase date is required";
    }
    if (!referenceNo.trim()) {
      newErrors.reference_no = "Reference number is required";
    }
    if (!selectedStatus || selectedStatus.value === "choose") {
      newErrors.status = "Status is required";
    }
    if (tableData.length === 0) {
      newErrors.products = "At least one product is required";
    } else {
      tableData.forEach((item, index) => {
        if (item.variants && item.variants.length > 0) {
          item.variants.forEach((variant, vIndex) => {
            if (!variant.qty || variant.qty <= 0) {
              newErrors[`product_${index}_variant_${vIndex}_qty`] =
                "Quantity must be greater than 0";
            }
            if (!variant.purchasePrice || variant.purchasePrice <= 0) {
              newErrors[`product_${index}_variant_${vIndex}_price`] =
                "Purchase price must be greater than 0";
            }
          });
        } else {
          if (!item.qty || item.qty <= 0) {
            newErrors[`product_${index}_qty`] =
              "Quantity must be greater than 0";
          }
          if (!item.purchasePrice || item.purchasePrice <= 0) {
            newErrors[`product_${index}_price`] =
              "Purchase price must be greater than 0";
          }
        }
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showToast("Please fill all required fields", "error", "Form Validation");
      return;
    }

    const totalAmount = tableData.reduce((sum, item) => {
      if (item.variants && item.variants.length > 0) {
        return sum + item.variants.reduce((vSum, v) => vSum + (v.totalCost || 0), 0);
      }
      return sum + (item.totalCost || 0);
    }, 0);

    const purchaseData = {
      ...(currentPurchase?.id && { id: currentPurchase.id }),
      supplier_id: selectedSupplier?.value || null,
      branch_id: selectedBranch?.value || null,
      purchase_date: selectedDateModal
        ? selectedDateModal.toISOString().split("T")[0]
        : null,
      reference_no: referenceNo || "",
      status: selectedStatus?.value || null,
      notes: notes || "",
      method: currentPurchase?.payment_method || "cash",
      total_amount: parseFloat(totalAmount.toFixed(2)) || 0,
      products: tableData.map((item) => {
        const baseProduct = {
          product_id: item.productId,
          quantity: item.variants.length > 0 ? 0 : item.qty || 0,
          purchase_price: item.variants.length > 0 ? 0 : item.purchasePrice || 0,
          unit_cost: item.variants.length > 0 ? 0 : item.unitCost || 0,
          total_cost: item.variants.length > 0 ? 0 : item.totalCost || 0,
        };

        baseProduct.variants = item.variants.map((v) => ({
          variant_id: v.id,
          quantity: v.qty || 0,
          purchase_price: v.purchasePrice || 0,
          unit_cost: v.unitCost || 0,
          total_cost: v.totalCost || 0,
        }));

        return baseProduct;
      }),
    };

    try {
      const response = await apiClient.post("/store-update-purchase", purchaseData);
      if (response.status) {
        setSelectedBranch(null);
        setSelectedProduct(null);
        setTableData([]);
        setSelectedSupplier(null);
        setReferenceNo("");
        setSelectedStatus(null);
        setNotes("");
        setErrors({});
        setSelectedDateModal(null);

        document.querySelector('[data-bs-dismiss="modal"]').click();
        if (onClose) onClose();
        if (onUpdate) onUpdate();
        showToast(
          `Purchase ${currentPurchase ? "updated" : "added"} successfully.`,
          "success",
          "Purchase Operation"
        );
      }
    } catch (error) {
      // console.error("Error submitting purchase:", error);
      showToast(
        error.message || `Failed to ${currentPurchase ? "update" : "add"} purchase`,
        "error",
        "Purchase Operation"
      );
    }
  };

  AddPurchases.propTypes = {
    currentPurchase: PropTypes.shape({
      id: PropTypes.number,
      supplier_id: PropTypes.number,
      branch_id: PropTypes.number,
      date: PropTypes.string,
      reference: PropTypes.string,
      status: PropTypes.string,
      note: PropTypes.string,
      payment_method: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          product_id: PropTypes.number,
          quantity: PropTypes.number,
          price: PropTypes.string,
          subtotal: PropTypes.string,
          variant_id: PropTypes.number,
        })
      ),
    }),
    onClose: PropTypes.func,
    onUpdate: PropTypes.func,
    canUpdate: PropTypes.bool,
    canCreate: PropTypes.bool,
  };

  const handleDateChangeModal = (date) => {
    setSelectedDateModal(date);
    setErrors((prev) => ({ ...prev, purchase_date: null }));
  };

  const handleBranchChange = (selected) => {
    setSelectedBranch(selected);
    setSelectedProduct(null);
    setTableData([]);
    setErrors((prev) => ({ ...prev, branch_id: null, products: null }));

    if (selected && selected.value !== "choose") {
      const branchProducts = allProducts
        .filter((product) => product.branch_id == selected.value)
        .map((product) => ({
          value: product.id,
          label: product.name,
          price: product.price || 0,
          variants: product.variants || [],
        }));
      setFilteredProducts(branchProducts);
    } else {
      setFilteredProducts([]);
    }
  };

  const handleProductChange = (selected) => {
    setSelectedProduct(selected);
    setErrors((prev) => ({ ...prev, products: null }));
    if (selected && selected.value !== "choose") {
      const productExists = tableData.some(
        (item) => item.productId === selected.value
      );
      if (!productExists) {
        const product = allProducts.find((p) => p.id === selected.value);
        const price = parseFloat(product.price) || 0;
        if (product.variants && product.variants.length > 0) {
          const newProduct = {
            productId: selected.value,
            product: selected.label,
            qty: 0,
            purchasePrice: 0,
            unitCost: 0,
            totalCost: 0,
            variants: product.variants.map((variant) => {
              const variantPrice = parseFloat(variant.price) || 0;
              return {
                id: variant.id,
                name: variant.name || "N/A",
                variantValue: variant.variant_value || "",
                qty: parseInt(variant.quantity) || 1,
                purchasePrice: variantPrice,
                unitCost: variantPrice,
                totalCost: variantPrice * (parseInt(variant.quantity) || 1),
              };
            }),
          };
          setTableData([...tableData, newProduct]);
        } else {
          const newProduct = {
            productId: selected.value,
            product: selected.label,
            qty: 1,
            purchasePrice: price,
            unitCost: price,
            totalCost: price * 1,
            variants: [],
          };
          setTableData([...tableData, newProduct]);
        }
      }
    }
    setSelectedProduct(null);
  };

  const handleRemoveVariant = (productIndex, variantIndex) => {
    const updatedData = [...tableData];
    if (updatedData[productIndex] && updatedData[productIndex].variants) {
      updatedData[productIndex].variants.splice(variantIndex, 1);
      if (updatedData[productIndex].variants.length === 0) {
        updatedData.splice(productIndex, 1);
      } else {
        updatedData[productIndex].totalCost = updatedData[productIndex].variants.reduce(
          (sum, v) => sum + (v.totalCost || 0),
          0
        );
      }
      setTableData(updatedData);
    }
  };

  const handleTableInputChange = (productIndex, variantIndex, field, value) => {
  const updatedData = [...tableData];
  if (!updatedData[productIndex]) {
    console.error(`Invalid productIndex: ${productIndex}`);
    return;
  }

  let item;
  if ( updatedData[productIndex].variants && updatedData[productIndex].variants.length > 0 ) {
    if (!updatedData[productIndex].variants[variantIndex]) {
      console.error(
        `Invalid variantIndex: ${variantIndex} for productIndex: ${productIndex}`
      );
      return;
    }
    item = updatedData[productIndex].variants[variantIndex];
  } else {
    item = updatedData[productIndex];
  }

  item[field] =
    value === ""
      ? null
      : field === "qty"
      ? parseInt(value) || 0
      : parseFloat(value) || 0;
  item.unitCost = item.purchasePrice !== null ? item.purchasePrice : 0;
  item.totalCost =
    (item.qty !== null ? item.qty : 0) *
    (item.unitCost !== null ? item.unitCost : 0);

  if ( updatedData[productIndex].variants && updatedData[productIndex].variants.length > 0 ) {
    updatedData[productIndex].totalCost = updatedData[
      productIndex
    ].variants.reduce((sum, v) => sum + (v.totalCost || 0), 0);
    updatedData[productIndex].qty = 0;
    updatedData[productIndex].purchasePrice = 0;
    updatedData[productIndex].unitCost = 0;
  }

  setTableData(updatedData);
  setErrors((prev) => ({
    ...prev,
    [`product_${productIndex}_variant_${variantIndex}_${field}`]: null,
    [`product_${productIndex}_${field}`]: null,
  }));
};

  const handleRemoveProduct = (index) => {
    const updatedData = tableData.filter((_, i) => i !== index);
    setTableData(updatedData);
  };

  return (
    <div>
      <div className="modal fade" id="add-units">
        <div className="modal-dialog purchase modal-dialog-centered stock-adjust-modal">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header border-0 custom-modal-header">
                  <div className="page-title">
                    <h4>Add Purchase</h4>
                  </div>
                  <button
                    type="button"
                    className="close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div className="modal-body custom-modal-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-lg-4 col-md-6 col-sm-12">
                        <div className="input-blocks">
                          <label>Supplier Name</label>
                          <Select
                            options={suppliers}
                            className="select"
                            placeholder="Choose Supplier"
                            value={selectedSupplier}
                            onChange={(selected) => {
                              setSelectedSupplier(selected);
                              setErrors((prev) => ({
                                ...prev,
                                supplier_id: null,
                              }));
                            }}
                          />
                          {errors.supplier_id && (
                            <span className="text-danger">
                              {errors.supplier_id}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="col-lg-4 col-md-6 col-sm-12">
                        <div className="input-blocks">
                          <label>Branch Name</label>
                          <Select
                            options={branches}
                            className="select"
                            placeholder="Choose Branch"
                            value={selectedBranch}
                            onChange={handleBranchChange}
                          />
                          {errors.branch_id && (
                            <span className="text-danger">
                              {errors.branch_id}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="col-lg-4 col-md-6 col-sm-12">
                        <div className="input-blocks">
                          <label>Product Name</label>
                          <Select
                            options={filteredProducts}
                            className="select"
                            placeholder="Choose Product"
                            onChange={handleProductChange}
                            value={selectedProduct}
                            isDisabled={!selectedBranch}
                          />
                          {errors.products && (
                            <span className="text-danger">
                              {errors.products}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="col-lg-4 col-md-6 col-sm-12">
                        <div className="input-blocks">
                          <label>Purchase Date</label>
                          <div className="input-group date-group" style={{ height: '54px' }}>
                            <span className="input-group-text">
                              <i className="bi bi-calendar-date"></i>
                            </span>
                            <DatePicker
                              selected={selectedDateModal}
                              onChange={handleDateChangeModal}
                              dateFormat="dd/MM/yyyy"
                              placeholderText="Choose Date"
                              className="form-control datetimepicker custom-datepicker"
                            />
                          </div>
                          {errors.purchase_date && (
                            <span className="text-danger">
                              {errors.purchase_date}
                            </span>
                          )}
                        </div>
                      </div>

                      <style jsx>{`
                        .custom-datepicker {
                          height: 54px !important;
                          line-height: 54px;
                        }
                      `}</style>

                      <div className="col-lg-4 col-md-6 col-sm-12">
                        <div className="input-blocks">
                          <label>Reference No</label>
                          <input
                            type="text"
                            className="form-control"
                            style={{ height: '54px' }}
                            value={referenceNo}
                            onChange={(e) => {
                              setReferenceNo(e.target.value);
                              setErrors((prev) => ({
                                ...prev,
                                reference_no: null,
                              }));
                            }}
                          />
                          {errors.reference_no && (
                            <span className="text-danger">
                              {errors.reference_no}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="col-lg-4 col-md-6 col-sm-12">
                        <div className="input-blocks">
                          <label>Status</label>
                          <Select
                            options={status}
                            className="select"
                            placeholder="Choose"
                            value={selectedStatus}
                            onChange={(selected) => {
                              setSelectedStatus(selected);
                              setErrors((prev) => ({ ...prev, status: null }));
                            }}
                          />
                          {errors.status && (
                            <span className="text-danger">{errors.status}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      {tableData.length > 0 && (
                        <div className="col-lg-12">
                          <div className="modal-body-table">
                            <div className="table-responsive">
                              <table className="table datanew">
                                <thead>
                                  <tr>
                                    <th>Product</th>
                                    <th>Variant</th>
                                    <th>Qty</th>
                                    <th>Purchase Price(PKR)</th>
                                    <th>Unit Cost(PKR)</th>
                                    <th>Total Cost(PKR)</th>
                                    <th>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {tableData.map((item, productIndex) =>
                                    item.variants &&
                                    item.variants.length > 0 ? (
                                      item.variants.map(
                                        (variant, variantIndex) => (
                                          <tr
                                            key={`${productIndex}-${variantIndex}`}
                                          >
                                            {variantIndex === 0 && (
                                              <td
                                                rowSpan={item.variants.length}
                                              >
                                                {item.product}
                                              </td>
                                            )}
                                            <td>
                                              {variant.name} (
                                              {variant.variantValue})
                                            </td>
                                            <td>
                                              <input
                                                type="number"
                                                className="form-control no-spinner"
                                                onWheel={(e) => e.target.blur()}
                                                value={
                                                  variant.qty === null
                                                    ? ""
                                                    : variant.qty
                                                }
                                                min="1"
                                                onChange={(e) =>
                                                  handleTableInputChange(
                                                    productIndex,
                                                    variantIndex,
                                                    "qty",
                                                    e.target.value
                                                  )
                                                }
                                              />
                                            </td>
                                            <td>
                                              <input
                                                type="number"
                                                className="form-control no-spinner"
                                                value={
                                                  variant.purchasePrice === null
                                                    ? ""
                                                    : variant.purchasePrice
                                                }
                                                onWheel={(e) => e.target.blur()}
                                                onChange={(e) =>
                                                  handleTableInputChange(
                                                    productIndex,
                                                    variantIndex,
                                                    "purchasePrice",
                                                    e.target.value
                                                  )
                                                }
                                                min="0"
                                                step="0.01"
                                              />
                                            </td>
                                            <td>
                                              {typeof variant.unitCost ===
                                              "number"
                                                ? variant.unitCost.toFixed(2)
                                                : "0.00"}
                                            </td>
                                            <td>
                                              {typeof variant.totalCost ===
                                              "number"
                                                ? variant.totalCost.toFixed(2)
                                                : "0.00"}
                                            </td>
<td>
                                              <button
                                                className="btn btn-danger btn-sm"
                                                onClick={() =>
                                                  handleRemoveVariant(productIndex, variantIndex)
                                                }
                                              >
                                                <i className="bi bi-trash"></i>
                                              </button>
                                            </td>
                                          </tr>
                                        )
                                      )
                                    ) : (
                                      <tr key={productIndex}>
                                        <td>{item.product}</td>
                                        <td>-</td>
                                        <td>
                                          <input
                                            type="number"
                                            className="form-control no-spinner"
                                            value={
                                              item.qty === null ? "" : item.qty
                                            }
                                            min="1"
                                            onWheel={(e) => e.target.blur()}
                                            onChange={(e) =>
                                              handleTableInputChange(
                                                productIndex,
                                                0,
                                                "qty",
                                                e.target.value
                                              )
                                            }
                                          />
                                        </td>
                                        <td>
                                          <input
                                            type="number"
                                            className="form-control no-spinner"
                                            value={
                                              item.purchasePrice === null
                                                ? ""
                                                : item.purchasePrice
                                            }
                                            onWheel={(e) => e.target.blur()}
                                            onChange={(e) =>
                                              handleTableInputChange(
                                                productIndex,
                                                0,
                                                "purchasePrice",
                                                e.target.value
                                              )
                                            }
                                            min="0"
                                            step="0.01"
                                          />
                                        </td>
                                        <td>
                                          {typeof item.unitCost === "number"
                                            ? item.unitCost.toFixed(2)
                                            : "0.00"}
                                        </td>
                                        <td>
                                          {typeof item.totalCost === "number"
                                            ? item.totalCost.toFixed(2)
                                            : "0.00"}
                                        </td>
                                        <td>
                                          <button
                                            className="btn btn-danger btn-sm"
                                            onClick={() =>
                                              handleRemoveProduct(productIndex)
                                            }
                                          >
                                            <i className="bi bi-trash"></i>
                                          </button>
                                        </td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="col-lg-12">
                      <div className="edit-add card">
                        <div className="edit-add">
                          <label className="form-label">Description</label>
                        </div>
                        <div className="card-body-list input-blocks mb-0">
                          <textarea
                            className="form-control"
                            name="note"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                          />
                        </div>
                      </div>
                      {/* <div className="input-blocks summer-description-box">
                                                <label>Notes</label>
                                                <div id="summernote" />
                                                <TextEditor value={notes} onChange={setNotes} />
                                            </div> */}
                    </div>
                    <div className="col-lg-12">
                      <div className="modal-footer-btn">
                        <button
                          type="button"
                          className="btn btn-cancel me-2"
                          data-bs-dismiss="modal"
                        >
                          Cancel
                        </button>

                        {currentPurchase
                          ? canUpdate && (
                              <button type="submit" className="btn btn-submit">
                                Update
                              </button>
                            )
                          :
                            canCreate && (
                              <button type="submit" className="btn btn-submit">
                                Submit
                              </button>
                            )}
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPurchases;
